import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Upload, Lock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUpload } from "@/hooks/use-upload";
import { CATEGORY_LABELS, formatFileSize } from "./documentCategories";

interface UploadBody {
  name: string;
  description: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  category: string;
  companyId?: string;
  contactId?: string;
  dealId?: string;
  isConfidential: boolean;
  tags: string[];
}

export function DocumentUploadDialog({ open, onOpenChange, target }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: { companyId?: string; contactId?: string; dealId?: string };
}) {
  const queryClient = useQueryClient();
  const { uploadFile, isUploading: isFileUploading } = useUpload();
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formCategory, setFormCategory] = useState<string>("other");
  const [formIsConfidential, setFormIsConfidential] = useState(false);
  const [formTags, setFormTags] = useState("");

  const resetForm = () => {
    setFormName("");
    setFormDescription("");
    setFormCategory("other");
    setFormIsConfidential(false);
    setFormTags("");
    setUploadingFile(null);
  };

  const uploadMutation = useMutation({
    mutationFn: async (data: UploadBody) => {
      const res = await fetch("/api/documents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Failed to upload document");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      toast.success("Document uploaded successfully");
      resetForm();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadingFile(file);
      setFormName(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleUpload = async () => {
    if (!uploadingFile) return;

    // Presigned two-step upload: bytes land in Supabase Storage, the document
    // row stores the durable /objects/... path (servable + attachable).
    const uploaded = await uploadFile(uploadingFile);
    if (!uploaded) {
      toast.error("File upload failed — nothing was saved. Try again.");
      return;
    }

    uploadMutation.mutate({
      name: formName,
      description: formDescription,
      fileName: uploadingFile.name,
      fileUrl: uploaded.objectPath,
      fileSize: uploadingFile.size,
      mimeType: uploadingFile.type,
      category: formCategory,
      companyId: target.companyId,
      contactId: target.contactId,
      dealId: target.dealId,
      isConfidential: formIsConfidential,
      tags: formTags.split(",").map((t) => t.trim()).filter(Boolean),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onOpenChange(false);
          resetForm();
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* File Drop Zone */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
              uploadingFile ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
            )}
          >
            {uploadingFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-primary-text" />
                <div className="text-left">
                  <p className="font-medium">{uploadingFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(uploadingFile.size)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setUploadingFile(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="font-medium">Click to upload or drag and drop</p>
                <p className="text-sm text-muted-foreground mt-1">
                  PDF, Word, Excel, images up to 50MB
                </p>
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*"
                />
              </label>
            )}
          </div>

          {/* Document Details */}
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Document Name</label>
              <Input
                placeholder="e.g., Teaser - Company Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description (optional)</label>
              <Textarea
                placeholder="Brief description of the document..."
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Tags (comma separated)</label>
              <Input
                placeholder="e.g., Q4-2024, draft, confidential"
                value={formTags}
                onChange={(e) => setFormTags(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isConfidential"
                checked={formIsConfidential}
                onChange={(e) => setFormIsConfidential(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="isConfidential" className="text-sm flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" />
                Mark as confidential
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!uploadingFile || !formName || isFileUploading || uploadMutation.isPending}
            className="gap-2"
          >
            <Upload className="w-4 h-4" />
            {isFileUploading ? "Uploading…" : "Upload Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
